import { useEffect, useMemo, useState } from 'react';

import {
  AvailableFokParameterChoiceViewModel,
  EmployeeViewModel,
  FokParameterType,
} from '@fora-monorepo/employer-my-pages-common/types';
import { Box, Checkbox, Container, Text, theme, useDatePickerInput } from '@foradevelopment/fora-ui';

import { useTypedTranslation } from '../../../i18n/i18n';
import { MarkupAndParametersFormProps } from './types';
import { getParameterEndDateMin, getParameterTKeys, validateParameterEndDate } from '../../../common/utils';
import { ParameterDateInputFields } from './ParameterDateInputFields';

/** TILLVAL_ASL65 AND TILLVAL_KP65 */
export const PensionParameterCheckbox = ({
  fokParameter,
  setParameterChoicesInput,
  disabled,
  employmentStartDate,
  employee,
  isSetAlready,
}: Pick<MarkupAndParametersFormProps, 'setParameterChoicesInput' | 'disabled' | 'employmentStartDate'> & {
  fokParameter: AvailableFokParameterChoiceViewModel;
  employee: EmployeeViewModel;
  isSetAlready?: boolean;
}) => {
  const { t } = useTypedTranslation();

  const [parameterChecked, setParameterChecked] = useState(false);
  const [endDateChecked, setEndDateChecked] = useState(false);

  const { parameterValue } = fokParameter;

  const datePickerInputSettings = useMemo(() => {
    return {
      initValue: fokParameter.endDate || '',
      minDate: getParameterEndDateMin(undefined, undefined, employmentStartDate),
      maxDate: undefined,
      validate: validateParameterEndDate(undefined, undefined),
    };
  }, [employmentStartDate, fokParameter.endDate]);

  const endDateInput = useDatePickerInput(datePickerInputSettings);

  useEffect(() => {
    // Update form data state
    setParameterChoicesInput(prevVal => {
      const otherParams = prevVal.filter(selected => selected.fokParameter.parameterValue !== parameterValue);
      if (!parameterChecked && !isSetAlready) {
        return otherParams;
      }

      return [
        ...otherParams,
        {
          fokParameter: {
            parameterValue: parameterValue as FokParameterType,
            endDate: endDateChecked || isSetAlready ? endDateInput.value || undefined : undefined,
          },
          validateInput: () =>
            endDateChecked || isSetAlready ? endDateInput.validateDateInputValue().length === 0 : true,
        },
      ];
    });
    // Only update when inputs have new value, new validation or box is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterChecked, endDateChecked, endDateInput.value, endDateInput.validateDateInputValue]);

  const toggleParameterCheckbox = () => {
    if (parameterChecked) {
      setParameterChecked(false);
      setEndDateChecked(false);
    } else {
      setParameterChecked(true);
    }
  };

  return (
    <Container>
      {isSetAlready && (
        <Container>
          <Text>{t(getParameterTKeys(parameterValue).activeLabel, { firstName: employee?.firstName })}</Text>
          <ParameterDateInputFields endDateInputProps={endDateInput.props} disabled={disabled} />
        </Container>
      )}
      {!isSetAlready && (
        <Checkbox
          key={parameterValue}
          id={parameterValue}
          checked={parameterChecked}
          onChange={toggleParameterCheckbox}
          disabled={disabled}
        >
          {t(getParameterTKeys(parameterValue).checkboxLabel)}
        </Checkbox>
      )}
      {parameterChecked && (
        <Box
          style={{ marginLeft: 30, marginTop: 10, border: `1px solid ${theme.colors.black20}` }}
          paddingLeft={[20, 40, 40]}
          paddingRight={[20, 40, 40]}
          paddingTop={20}
          paddingBottom={20}
          backgroundColor="black05"
        >
          <Text marginBottom={10}>{t('form.parameter.validFromText')}</Text>

          <Checkbox
            key={parameterValue + '-pension-parameter-end-date-confirmation'}
            id={parameterValue + '-pension-parameter-end-date-confirmation'}
            checked={endDateChecked}
            onChange={() => setEndDateChecked(!endDateChecked)}
            disabled={disabled}
          >
            {t('form.parameter.setEndDate')}
          </Checkbox>
          {endDateChecked && <ParameterDateInputFields endDateInputProps={endDateInput.props} disabled={disabled} />}
        </Box>
      )}
    </Container>
  );
};
